import {
  adjustedCreatorFeeRate,
  quoteCpSwapBaseInput,
  resolveCreatorFeeOnInput,
} from './cp-swap.js';
import {
  type DisplayMarkVenue,
  displayCurrentValue,
  displayMarkedOutcomeAmount,
  displayVenue,
} from './display-mark.js';
import {
  available,
  BPS_DENOMINATOR,
  floorFee,
  isI64,
  isNonNegativeI64,
  isSignedMagnitudeU128,
  isU64,
  unavailable,
} from './integer.js';
import {
  type OutcomePoolQuoteContext,
  penaltyTargetAtQuoteTime,
  quoteOutcomeSellExactIn,
} from './outcome-pool.js';
import { isPenaltySell } from './penalty.js';
import type { PoolMathResult, PoolMathUnavailableReason } from './types.js';

export interface PnlBreakdownInput {
  context: OutcomePoolQuoteContext;
  outcomeBalanceD6: bigint;
  claimRightsBalanceD6: bigint;
  remainingCostBasisGrossD6: bigint;
  remainingCostBasisNetD6: bigint;
  realizedPnlGrossD6: bigint;
  realizedPnlNetD6: bigint;
  /** Penalty already paid on this position's sells (part of the realised fees). */
  realizedPenaltyD6: bigint;
  prizeClaimableD6: bigint;
}

/**
 * The one P&L shape every screen renders from. Gross is fee-excluded, net is
 * after every fee the chain would take; the fee lines in between let a client
 * show either without a second valuation. Current value is the exact
 * full-exit quote for the marked (sellable) amount through the pool the
 * lifecycle selects, ignoring execution eligibility such as open-time and
 * conviction gates: a paused pool still has a value.
 */
export interface PnlBreakdown {
  venue: DisplayMarkVenue;
  markedOutcomeAmountD6: bigint;
  /**
   * Why the marked amount cannot be sold right now (pool not open, swap
   * paused, conviction gate, below minimum), or null when an exit would
   * execute. The value above is quoted either way.
   */
  exitGate: PoolMathUnavailableReason | null;
  realizedGrossD6: bigint;
  realizedNetD6: bigint;
  /** Fees already paid on realised trades (`realizedGross - realizedNet`). */
  realizedFeesD6: bigint;
  /** Penalty share of `realizedFeesD6`. */
  realizedPenaltyD6: bigint;
  costBasisGrossD6: bigint;
  costBasisNetD6: bigint;
  currentValueGrossD6: bigint;
  /** Base and prize-pool fee on a bonding curve; trade and creator fee on an AMM. */
  exitFeesD6: bigint;
  /** Extra bonding-curve fee charged for selling a non-leading outcome. */
  exitPenaltyD6: bigint;
  currentValueNetD6: bigint;
  prizeClaimableD6: bigint;
  unrealizedGrossD6: bigint;
  unrealizedNetD6: bigint;
  totalGrossD6: bigint;
  totalNetD6: bigint;
}

/**
 * How the still-open part of a position is valued. The realised leg is never
 * a choice: it is the fee-free cash result less the conviction penalty the
 * wallet paid for selling a non-leading outcome. Base, prize-pool and entry
 * fees are platform costs and stay out of P&L; the penalty is the price of
 * betting against the crowd, so it counts. Every surface and the leaderboard
 * report this same figure.
 *
 *   - `mark` values the open position at its exit quote before fees, the way
 *     a brokerage marks an open lot. It moves only when the price moves.
 *   - `exit_now` values it at what the wallet would actually receive, after
 *     the exit fees and the non-leader penalty. This one can move without a
 *     price change — the penalty turns on when the outcome stops leading —
 *     so it belongs next to a sell action, not in a headline.
 */
export type PnlDisplayMode = 'mark' | 'exit_now';

/** The headline view every screen renders; `exit_now` is the companion line. */
export const POSITION_PNL_VIEW_MODE: PnlDisplayMode = 'mark';

export interface PnlView {
  mode: PnlDisplayMode;
  /** Fee-free realised result less the conviction penalty; identical in both modes. */
  realizedD6: bigint;
  /** Conviction penalty already paid on this position's sells; part of `realizedD6`. */
  penaltyD6: bigint;
  /** Fee-free basis: what the wallet paid for the open lot before entry fees. */
  costBasisD6: bigint;
  currentValueD6: bigint;
  unrealizedD6: bigint;
  totalD6: bigint;
}

/** The legs a P&L view is built from; both the per-position and the consolidated breakdown carry them. */
export type PnlViewLegs = Pick<
  PnlBreakdown,
  | 'realizedGrossD6'
  | 'realizedPenaltyD6'
  | 'costBasisGrossD6'
  | 'currentValueGrossD6'
  | 'currentValueNetD6'
  | 'prizeClaimableD6'
>;

export function buildPnlBreakdown(input: PnlBreakdownInput): PoolMathResult<PnlBreakdown> {
  // Checked before the range gate below so a replay deficit reports itself
  // rather than reading as a malformed amount.
  if (input.claimRightsBalanceD6 < 0n) return unavailable('claim_rights_replay_deficit');
  if (
    !isU64(input.outcomeBalanceD6) ||
    !isU64(input.claimRightsBalanceD6) ||
    !isU64(input.prizeClaimableD6) ||
    !isNonNegativeI64(input.remainingCostBasisGrossD6) ||
    !isNonNegativeI64(input.remainingCostBasisNetD6) ||
    !isI64(input.realizedPnlGrossD6) ||
    !isI64(input.realizedPnlNetD6) ||
    !isU64(input.realizedPenaltyD6) ||
    !isU64(input.context.chainClock.slot) ||
    !isU64(input.context.chainClock.unixTimestampSeconds)
  ) {
    return unavailable('invalid_amount');
  }
  const venue = displayVenue(input.context);
  if (!venue.available) return venue;
  const marked = displayMarkedOutcomeAmount(
    input.context,
    input.outcomeBalanceD6,
    input.claimRightsBalanceD6,
    venue.value,
  );
  if (!marked.available) return marked;
  const gross = displayCurrentValue(input.context, venue.value, marked.value);
  if (!gross.available) return gross;
  const fees = exitFeesForMarkedAmount(input.context, venue.value, marked.value, gross.value);
  if (!fees.available) return fees;
  const exitGate = exitGateFor(input.context, venue.value, marked.value);

  const currentValueNetD6 = gross.value - fees.value.feesD6 - fees.value.penaltyD6;
  const unrealizedGrossD6 = gross.value + input.prizeClaimableD6 - input.remainingCostBasisGrossD6;
  const unrealizedNetD6 =
    currentValueNetD6 + input.prizeClaimableD6 - input.remainingCostBasisNetD6;
  const totalGrossD6 = input.realizedPnlGrossD6 + unrealizedGrossD6;
  const totalNetD6 = input.realizedPnlNetD6 + unrealizedNetD6;
  if (
    !isSignedMagnitudeU128(unrealizedGrossD6) ||
    !isSignedMagnitudeU128(unrealizedNetD6) ||
    !isSignedMagnitudeU128(totalGrossD6) ||
    !isSignedMagnitudeU128(totalNetD6)
  ) {
    return unavailable('invalid_amount');
  }
  return available({
    venue: venue.value,
    markedOutcomeAmountD6: marked.value,
    exitGate,
    realizedGrossD6: input.realizedPnlGrossD6,
    realizedNetD6: input.realizedPnlNetD6,
    realizedFeesD6: input.realizedPnlGrossD6 - input.realizedPnlNetD6,
    realizedPenaltyD6: input.realizedPenaltyD6,
    costBasisGrossD6: input.remainingCostBasisGrossD6,
    costBasisNetD6: input.remainingCostBasisNetD6,
    currentValueGrossD6: gross.value,
    exitFeesD6: fees.value.feesD6,
    exitPenaltyD6: fees.value.penaltyD6,
    currentValueNetD6,
    prizeClaimableD6: input.prizeClaimableD6,
    unrealizedGrossD6,
    unrealizedNetD6,
    totalGrossD6,
    totalNetD6,
  });
}

export function selectPnlView(breakdown: PnlViewLegs, mode: PnlDisplayMode): PnlView {
  const penaltyD6 = breakdown.realizedPenaltyD6;
  const realizedD6 = breakdown.realizedGrossD6 - penaltyD6;
  const costBasisD6 = breakdown.costBasisGrossD6;
  const currentValueD6 =
    mode === 'exit_now' ? breakdown.currentValueNetD6 : breakdown.currentValueGrossD6;
  const unrealizedD6 = currentValueD6 + breakdown.prizeClaimableD6 - costBasisD6;
  return {
    mode,
    realizedD6,
    penaltyD6,
    costBasisD6,
    currentValueD6,
    unrealizedD6,
    totalD6: realizedD6 + unrealizedD6,
  };
}

/** A server-computed `PnlBreakdown` as it arrives on the wire: every field a string. */
export type PnlBreakdownWire = Record<keyof PnlBreakdown, string | null | undefined>;

/**
 * Parses a server-computed breakdown back into the shape `buildPnlBreakdown`
 * returns. Use it wherever there is no live pool snapshot to quote against —
 * the server row IS the valuation there, never a `balance * spot`
 * re-derivation. Returns null when a field is missing or out of range.
 */
export function pnlBreakdownFromWire(wire: PnlBreakdownWire): PnlBreakdown | null {
  const venue = wireVenue(wire.venue);
  const markedOutcomeAmountD6 = unsignedWire(wire.markedOutcomeAmountD6);
  const realizedGrossD6 = signedWire(wire.realizedGrossD6);
  const realizedNetD6 = signedWire(wire.realizedNetD6);
  const realizedFeesD6 = signedWire(wire.realizedFeesD6);
  const realizedPenaltyD6 = unsignedWire(wire.realizedPenaltyD6);
  const costBasisGrossD6 = unsignedWire(wire.costBasisGrossD6);
  const costBasisNetD6 = unsignedWire(wire.costBasisNetD6);
  const currentValueGrossD6 = unsignedWire(wire.currentValueGrossD6);
  const exitFeesD6 = unsignedWire(wire.exitFeesD6);
  const exitPenaltyD6 = unsignedWire(wire.exitPenaltyD6);
  const currentValueNetD6 = signedWire(wire.currentValueNetD6);
  const prizeClaimableD6 = unsignedWire(wire.prizeClaimableD6);
  const unrealizedGrossD6 = signedMagnitudeWire(wire.unrealizedGrossD6);
  const unrealizedNetD6 = signedMagnitudeWire(wire.unrealizedNetD6);
  const totalGrossD6 = signedMagnitudeWire(wire.totalGrossD6);
  const totalNetD6 = signedMagnitudeWire(wire.totalNetD6);
  if (
    venue === null ||
    markedOutcomeAmountD6 === null ||
    realizedGrossD6 === null ||
    realizedNetD6 === null ||
    realizedFeesD6 === null ||
    realizedPenaltyD6 === null ||
    costBasisGrossD6 === null ||
    costBasisNetD6 === null ||
    currentValueGrossD6 === null ||
    exitFeesD6 === null ||
    exitPenaltyD6 === null ||
    currentValueNetD6 === null ||
    prizeClaimableD6 === null ||
    unrealizedGrossD6 === null ||
    unrealizedNetD6 === null ||
    totalGrossD6 === null ||
    totalNetD6 === null
  ) {
    return null;
  }
  return {
    venue,
    markedOutcomeAmountD6,
    // Quoted informationally (why the marked amount cannot sell right now) and
    // passed through untyped from the wire; no reader re-derives from it.
    exitGate: (wire.exitGate ?? null) as PoolMathUnavailableReason | null,
    realizedGrossD6,
    realizedNetD6,
    realizedFeesD6,
    realizedPenaltyD6,
    costBasisGrossD6,
    costBasisNetD6,
    currentValueGrossD6,
    exitFeesD6,
    exitPenaltyD6,
    currentValueNetD6,
    prizeClaimableD6,
    unrealizedGrossD6,
    unrealizedNetD6,
    totalGrossD6,
    totalNetD6,
  };
}

function wireVenue(value: string | null | undefined): DisplayMarkVenue | null {
  if (value === 'bonding_curve' || value === 'cp_swap' || value === 'terminal') return value;
  return null;
}

function unsignedWire(value: string | null | undefined): bigint | null {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
  const parsed = BigInt(value);
  return isNonNegativeI64(parsed) ? parsed : null;
}

function signedWire(value: string | null | undefined): bigint | null {
  if (typeof value !== 'string' || !/^-?\d+$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}

function signedMagnitudeWire(value: string | null | undefined): bigint | null {
  if (typeof value !== 'string' || !/^-?\d+$/.test(value)) return null;
  const parsed = BigInt(value);
  return isSignedMagnitudeU128(parsed) ? parsed : null;
}

export interface ExitFees {
  feesD6: bigint;
  penaltyD6: bigint;
}

function exitGateFor(
  context: OutcomePoolQuoteContext,
  venue: DisplayMarkVenue,
  markedOutcomeAmountD6: bigint,
): PoolMathUnavailableReason | null {
  if (venue === 'terminal' || markedOutcomeAmountD6 === 0n) return null;
  const quote = quoteOutcomeSellExactIn({ context, outcomeAmountInD6: markedOutcomeAmountD6 });
  return quote.available ? null : quote.reason;
}

/** Fees the chain would take on selling `markedOutcomeAmountD6` for `grossValueD6`. */
export function exitFeesForMarkedAmount(
  context: OutcomePoolQuoteContext,
  venue: DisplayMarkVenue,
  markedOutcomeAmountD6: bigint,
  grossValueD6: bigint,
): PoolMathResult<ExitFees> {
  if (venue === 'terminal' || markedOutcomeAmountD6 === 0n) {
    return available({ feesD6: 0n, penaltyD6: 0n });
  }
  if (venue === 'bonding_curve') {
    if (context.poolModel !== 'bonding_curve') return unavailable('missing_bonding_curve_state');
    const { fees } = context.bondingCurve;
    const penalty = penaltyTargetAtQuoteTime(context);
    if (!penalty.available) return penalty;
    const baseFeeD6 = floorFee(grossValueD6, fees.sellBaseFeeBps, BPS_DENOMINATOR);
    const prizePoolFeeD6 = floorFee(grossValueD6, fees.prizePoolFeeBps, BPS_DENOMINATOR);
    const penaltyD6 = isPenaltySell(penalty.value.targetOutcomeIndex, context.venue.outcomeIndex)
      ? floorFee(grossValueD6, fees.sellPenaltyFeeBps, BPS_DENOMINATOR)
      : 0n;
    return available({ feesD6: baseFeeD6 + prizePoolFeeD6, penaltyD6 });
  }
  if (context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  const pool = context.cpSwap;
  const net = quoteCpSwapBaseInput({
    inputAmountD6: markedOutcomeAmountD6,
    inputReserveD6: pool.outcomeReserveD6,
    outputReserveD6: pool.quoteReserveD6,
    tradeFeeRateMillionths: pool.tradeFeeRateMillionths,
    creatorFeeRateMillionths: adjustedCreatorFeeRate(pool),
    creatorFeeOnInput: resolveCreatorFeeOnInput(pool.creatorFeeOn, pool.outcomeTokenIndex),
  });
  if (!net.available) return net;
  return available({ feesD6: grossValueD6 - net.value.outputAmountD6, penaltyD6: 0n });
}
